import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import c from 'classnames';

import { Icon, Typography } from '@repo/ui';
import type { FilterConfig } from 'components/Filters/NewFilters';
import { NewFilters } from 'components/Filters/NewFilters';

import { getEvaluationIconFromColor } from '../EvaluationGroupsSummary/EvaluationGroupsSummary';
import type { InsightMetric } from 'types/Insights';

import styles from './CategoryMetricsFilter.module.scss';

const MetricFilter = ({
  value,
  display,
}: {
  value: string;
  display: string;
}) => {
  return (
    <>
      <Icon
        name={getEvaluationIconFromColor(value)}
        size='s'
        className={c(styles.icon, styles[value])}
      />
      <Typography variant='label'>{display}</Typography>
    </>
  );
};

const baseEvaluationFiltersConfig = [
  {
    value: 'red',
    display: <MetricFilter value='red' display='Needs support' />,
  },
  {
    value: 'orange',
    display: <MetricFilter value='orange' display='Needs improvement' />,
  },
  {
    value: 'grey',
    display: <MetricFilter value='grey' display='Normal results' />,
  },
  {
    value: 'green',
    display: <MetricFilter value='green' display='Great results' />,
  },
];

type CategoryMetricsFilterProps = {
  submetrics: InsightMetric[];
  setFiltered: Dispatch<SetStateAction<string[]>>;
};

export const CategoryMetricsFilter = ({
  submetrics,
  setFiltered,
}: CategoryMetricsFilterProps) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const evaluationCounts = useMemo(() => {
    const counts: Record<string, number> = {
      red: 0,
      orange: 0,
      grey: 0,
      green: 0,
    };
    if (!submetrics) return counts;

    submetrics.forEach(submetric => {
      if (counts[submetric.evaluation_color] !== undefined) {
        counts[submetric.evaluation_color]++;
      }
    });
    return counts;
  }, [submetrics]);

  const evaluationFiltersWithCounts: FilterConfig[] = useMemo(() => {
    return baseEvaluationFiltersConfig.map(filter => ({
      ...filter,
      count: evaluationCounts[filter.value] ?? 0,
    }));
  }, [evaluationCounts]);

  useEffect(() => {
    if (setFiltered) {
      if (!activeFilters) {
        setFiltered([]);
      } else {
        setFiltered(activeFilters);
      }
    }
  }, [activeFilters, setFiltered]);

  return (
    <NewFilters
      name='evaluations'
      filters={activeFilters}
      filterConfig={evaluationFiltersWithCounts}
      setFiltered={setActiveFilters}
    />
  );
};
